import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const FAQSection: React.FC = () => {
  const { t } = useTranslation();
  const [openItem, setOpenItem] = useState<number | null>(null);

  const toggleItem = (index: number) => {
    setOpenItem(prev => prev === index ? null : index);
  };

  const faqData = t('landing.faq.questions', { returnObjects: true }) as Array<{
    question: string;
    answer: string;
  }> || [];
  
  // Fallback if translation fails
  if (!Array.isArray(faqData) || faqData.length === 0) {
    console.warn('FAQ translations not loaded properly, using fallback');
  }
  return (
    <section id="faq" className="py-20 bg-slate-900/30 mt-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl">
            <HelpCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t('landing.faq.title').split(' ')[0]}{' '}
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              {t('landing.faq.title').split(' ')[1]}
            </span>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            {t('landing.faq.subtitle').split('CVMatchSwiss')[0]}
            <span className="text-indigo-400 font-semibold">CVMatch</span>
            {t('landing.faq.subtitle').split('CVMatchSwiss')[1]}
          </p>
        </div>

        <div className="space-y-4">
          {Array.isArray(faqData) && faqData.length > 0 ? faqData.map((item, index) => {
            const isOpen = openItem === index;
            return (
              <div
                key={index}
                className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl overflow-hidden hover:border-indigo-500/50 transition-all duration-300 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-slate-700/30 transition-colors duration-200 group"
                >
                  <h3 className="text-lg font-semibold text-white pr-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-indigo-400 group-hover:to-purple-400 group-hover:bg-clip-text transition-all duration-300">
                    {item.question}
                  </h3>
                  <div className="flex-shrink-0">
                    {isOpen ? (
                      <ChevronUp className="w-5 h-5 text-indigo-400 group-hover:scale-110 transition-transform duration-200" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400 group-hover:text-indigo-400 group-hover:scale-110 transition-all duration-200" />
                    )}
                  </div>
                </button>
                
                {isOpen && (
                  <div className="px-6 pb-6 animate-fade-in">
                    <div className="border-t border-slate-600/30 pt-4">
                      <p className="text-slate-300 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          }) : (
            <div className="text-center text-slate-300">
              <p>FAQ content is loading...</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;